import { useEffect, useRef, useState } from 'react'
import { useGetUserSession, useSignOut } from '@/shared/hooks/api/auth'
import type { Organization } from '@/shared/hooks/api/organization'
import {
  useCompleteOnboarding,
  useCurrentOrganization,
  useUpdateOrganization,
} from '@/shared/hooks/api/organization'
import { useUpdateName } from '@/shared/hooks/api/user'
import { Check, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { ArtemLogoSymbol } from '@/components/artem-logo-symbol'
import { ChinaPsbFilingFooter } from '@/components/china-psb-filing-footer'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { TempLogoType } from '@/components/temp-logo-type'
import { ThemeSwitch } from '@/components/theme-switch'
import { AuthInterfaceLanguageSelect } from '@/features/auth/auth-interface-language-select'
import type { AddressFormValues } from './onboarding-address-step'
import { OnboardingAddressStep } from './onboarding-address-step'
import { OnboardingEmailVerificationStep } from './onboarding-email-verification-step'
import { OnboardingTermsStep } from './onboarding-terms-step'

type WizardStep = 1 | 2 | 3

function hasPersistedOnboardingAddress(organization: Organization): boolean {
  const street = organization.street?.trim() ?? ''
  const streetNumber = organization.streetNumber?.trim() ?? ''
  const city = organization.city?.trim() ?? ''
  const country = organization.country?.trim() ?? ''
  const postCode = organization.postCode?.trim() ?? ''
  return (
    street.length > 0 &&
    streetNumber.length > 0 &&
    city.length > 0 &&
    country.length > 0 &&
    postCode.length > 0
  )
}

function computeInitialStep(
  emailVerifiedAt: string | null | undefined,
  organization: Organization | undefined,
  isWeChatOnlyUser: boolean
): WizardStep {
  if (isWeChatOnlyUser) {
    if (organization && hasPersistedOnboardingAddress(organization)) {
      return 3
    }
    return 2
  }

  if (!emailVerifiedAt) {
    return 1
  }
  if (organization && hasPersistedOnboardingAddress(organization)) {
    return 3
  }
  return 2
}

type OnboardingStepMeta = {
  step: WizardStep
  displayStep: number
  title: string
  description: string
}

function OnboardingStepsOverview({
  currentStep,
  isWeChatOnlyUser,
}: {
  currentStep: WizardStep
  isWeChatOnlyUser: boolean
}) {
  const { t } = useTranslation('onboarding')

  const stepsMeta: OnboardingStepMeta[] = isWeChatOnlyUser
    ? [
        {
          step: 2,
          displayStep: 1,
          title: t('stepAddressTitle'),
          description: t('stepAddressDescriptionWeChat'),
        },
        {
          step: 3,
          displayStep: 2,
          title: t('stepTermsTitle'),
          description: t('stepTermsDescriptionWeChat'),
        },
      ]
    : [
        {
          step: 1,
          displayStep: 1,
          title: t('stepVerifyEmailTitle'),
          description: t('stepVerifyEmailDescription'),
        },
        {
          step: 2,
          displayStep: 2,
          title: t('stepAddressTitle'),
          description: t('stepAddressDescription'),
        },
        {
          step: 3,
          displayStep: 3,
          title: t('stepTermsTitle'),
          description: t('stepTermsDescription'),
        },
      ]

  return (
    <ol className='space-y-0' aria-label={t('overviewAriaLabel')}>
      {stepsMeta.map((item, index) => {
        const isComplete = currentStep > item.step
        const isCurrent = currentStep === item.step
        const isLast = index === stepsMeta.length - 1

        return (
          <li
            key={item.step}
            className='flex gap-4'
            aria-current={isCurrent ? 'step' : undefined}
          >
            <div className='flex flex-col items-center'>
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                  isComplete &&
                    'border-primary bg-primary text-primary-foreground',
                  isCurrent &&
                    !isComplete &&
                    'border-primary bg-background text-primary',
                  !isCurrent &&
                    !isComplete &&
                    'border-muted-foreground/35 text-muted-foreground'
                )}
              >
                {isComplete ? (
                  <Check className='size-4' strokeWidth={2.5} aria-hidden />
                ) : (
                  item.displayStep
                )}
              </span>
              {!isLast && (
                <span
                  className={cn(
                    'my-2 min-h-14 w-px shrink-0',
                    isComplete ? 'bg-primary' : 'bg-border'
                  )}
                  aria-hidden
                />
              )}
            </div>
            <div className={cn('min-w-0 pb-10', isLast && 'pb-0')}>
              <p
                className={cn(
                  'leading-snug font-medium',
                  isCurrent && 'text-foreground',
                  !isCurrent && 'text-muted-foreground'
                )}
              >
                {item.title}
              </p>
              <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                {item.description}
              </p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function getStepTitle(
  step: WizardStep,
  translate: (key: string) => string
): string {
  if (step === 1) {
    return translate('stepVerifyEmailTitle')
  }
  if (step === 2) {
    return translate('stepAddressTitle')
  }
  return translate('stepTermsTitle')
}

function getStepDescription(
  step: WizardStep,
  translate: (key: string) => string,
  isWeChatOnlyUser: boolean
): string {
  if (step === 1) {
    return translate('stepVerifyEmailDescription')
  }
  if (step === 2) {
    if (isWeChatOnlyUser) {
      return translate('stepAddressDescriptionWeChat')
    }
    return translate('stepAddressDescription')
  }
  if (isWeChatOnlyUser) {
    return translate('stepTermsDescriptionWeChat')
  }
  return translate('stepTermsDescription')
}

export function OnboardingWizard() {
  const { t } = useTranslation('onboarding')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false)
  const { mutate: signOut, isPending: isSigningOut } = useSignOut()
  const { data: session, refetch: refetchSession } = useGetUserSession()
  const { data: organization, isLoading: isOrganizationLoading } =
    useCurrentOrganization()

  const isWeChatOnlyUser = session?.isWeChatOnlyUser === true

  const [step, setStep] = useState<WizardStep>(() =>
    computeInitialStep(
      session?.emailVerifiedAt,
      organization,
      session?.isWeChatOnlyUser === true
    )
  )
  const hasAppliedInitialStep = useRef(session != null && organization != null)

  useEffect(() => {
    if (!session || !organization || hasAppliedInitialStep.current) {
      return
    }
    hasAppliedInitialStep.current = true
    setStep(
      computeInitialStep(
        session.emailVerifiedAt,
        organization,
        session.isWeChatOnlyUser === true
      )
    )
  }, [session, organization])

  useEffect(() => {
    if (isWeChatOnlyUser && step === 1) {
      setStep(2)
    }
  }, [isWeChatOnlyUser, step])

  const { mutateAsync: updateOrganization, isPending: isUpdating } =
    useUpdateOrganization()
  const { mutateAsync: updateName, isPending: isUpdatingName } = useUpdateName()
  const { mutateAsync: completeOnboarding, isPending: isCompleting } =
    useCompleteOnboarding()

  async function onEmailVerified() {
    await refetchSession()
    setStep(2)
  }

  async function onAddressSubmit(data: AddressFormValues) {
    if (!organization) {
      return
    }

    if (isWeChatOnlyUser) {
      const weChatData = data as AddressFormValues & {
        firstName: string
        lastName: string
        organizationName: string
        contactPhoneNumber: string
      }

      await updateName({
        firstName: weChatData.firstName.trim(),
        lastName: weChatData.lastName.trim(),
      })
    }

    await updateOrganization({
      organizationId: organization.id,
      payload: {
        ...(isWeChatOnlyUser
          ? {
              name: (
                data as AddressFormValues & { organizationName: string }
              ).organizationName.trim(),
              contactPhoneNumber: (
                data as AddressFormValues & { contactPhoneNumber: string }
              ).contactPhoneNumber.trim(),
            }
          : {}),
        street: data.street.trim(),
        streetNumber: data.streetNumber.trim(),
        city: data.city.trim(),
        country: data.country.trim().toLowerCase(),
        postCode: data.postCode.trim(),
      },
    })

    toast.success(t('addressSaved'))
    setStep(3)
  }

  async function onTermsSubmit() {
    if (!organization) {
      return
    }

    await completeOnboarding(organization.id)
    toast.success(t('completed'))
    navigate('/', { replace: true })
  }

  const isBusy =
    isOrganizationLoading || isUpdating || isUpdatingName || isCompleting

  return (
    <>
      <div className='flex min-h-svh flex-col'>
        <div className='grid min-h-0 w-full flex-1 grid-cols-1 lg:grid-cols-[minmax(280px,400px)_minmax(0,1fr)]'>
        <aside
          className={cn(
            'flex min-h-svh flex-col border-b border-border bg-muted/40 px-5 py-8 sm:px-8',
            'lg:min-h-0 lg:border-e lg:border-b-0 lg:py-12 lg:ps-10 lg:pe-8'
          )}
        >
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='mb-8 flex items-center'>
              <ArtemLogoSymbol className='size-10 shrink-0 text-[#02FFD8]' />
              <TempLogoType className='h-4 shrink-0' />
            </div>
            <h1 className='text-xl font-semibold tracking-tight sm:text-2xl'>
              {t('title')}
            </h1>
            <div className='mt-8'>
              <OnboardingStepsOverview
                currentStep={step}
                isWeChatOnlyUser={isWeChatOnlyUser}
              />
            </div>
          </div>
          <div className='mt-auto flex shrink-0 flex-col gap-4 pt-10'>
            <Button
              type='button'
              variant='ghost'
              className='h-auto max-w-[220px] justify-start gap-2 px-0 text-muted-foreground hover:text-foreground'
              onClick={() => {
                setIsSignOutDialogOpen(true)
              }}
            >
              <LogOut className='size-4 shrink-0' aria-hidden />
              {t('signOut')}
            </Button>
            <div className='flex max-w-[240px] justify-between gap-3 sm:max-w-[280px]'>
              <AuthInterfaceLanguageSelect className='max-w-[220px]' />
              <ThemeSwitch />
            </div>
          </div>
        </aside>

        <main className='flex min-h-0 flex-col overflow-y-auto'>
          <div className='mx-auto flex w-full max-w-lg flex-1 flex-col px-4 py-8 sm:max-w-xl sm:px-8 lg:py-12'>
            <header className='mb-8 space-y-2'>
              <h2 className='text-xl font-semibold tracking-tight sm:text-2xl'>
                {getStepTitle(step, t)}
              </h2>
              <p className='text-sm leading-relaxed text-muted-foreground sm:text-base'>
                {getStepDescription(step, t, isWeChatOnlyUser)}
              </p>
            </header>
            {step === 1 && !isWeChatOnlyUser && (
              <OnboardingEmailVerificationStep
                email={session?.email ?? ''}
                isBusy={isBusy}
                onVerified={onEmailVerified}
              />
            )}
            {step === 2 && (
              <OnboardingAddressStep
                includeWeChatProfileFields={isWeChatOnlyUser}
                isBusy={isBusy}
                onSubmit={onAddressSubmit}
              />
            )}
            {step === 3 && (
              <OnboardingTermsStep
                isBusy={isBusy}
                onBack={() => {
                  setStep(2)
                }}
                onComplete={onTermsSubmit}
              />
            )}
          </div>
        </main>
        </div>

        <ChinaPsbFilingFooter className='shrink-0' />
      </div>

      <ConfirmDialog
        open={isSignOutDialogOpen}
        onOpenChange={setIsSignOutDialogOpen}
        title={t('signOutDialogTitle')}
        desc={t('signOutDialogDescription')}
        cancelBtnText={tCommon('actions.cancel')}
        confirmText={tCommon('actions.signOut')}
        destructive
        isLoading={isSigningOut}
        handleConfirm={() => {
          signOut()
        }}
      />
    </>
  )
}
